/** Allows an Error to be thrown from a ternary, null-coalescing or object initialiser, e.g.
 *
 * const config = {
 *    prop1: 5,
 *    prop2: process.env.SOMETHING ?? throwError('Environment variable SOMETHING was not defined'),
 *    prop3: process.env.SOMETHING_ELSE ?? 'SensibleDefault'
 * };
 *
 */
export function throwError(
  message: string,
  options?: ErrorOptions | undefined,
): never {
  throw new Error(message, options);
}

/** The type returned from peekError. See peekError for more details */
export type PeekErrorWrapper = undefined | IPeekErrorWrapperObject;
/** Represents an object that is definitely not null, and has string keys and a toString function. Basically, almost any
 * javascript object/primitive will satisfy this type.
 *
 * This interface should not need to be public. */
interface IPeekErrorWrapperObject {
  [key: string]: undefined | IPeekErrorWrapperObject;

  /* Ignore the error, so that autocomplete lists toString() on the wrapped error. Normally, an object
  cannot contain a key-value pair who's value doesn't match the object's indexer value type.
  
  NOTE - The @ts-expect-error must appear in a separate block comment (rather than single-line comment) so that it is
  preserved in the built .d.ts */
  /** @ts-expect-error - Allow TS2411 for better intellisense. All JavaScript objects do actually have a .toString() */
  toString: () => string;
}

/** Changes the type of an unknown object to one that can be traversed easily using simple ?. operators and .toString.
 * This does not actually wrap the object instance, instead it just does a type cast to IPeekErrorWrapperObject.
 *
 * This allows us to easily traverse error object without have to do lots of casting and assertions. It also forces us
 * to use the ?. (optional-chaining operator). If we used 'any' to inspect the error, then we could unintentionally try
 * and access a property on a null or undefined part of the error object
 *
 * e.g. we can do
 *
 * try {
 * }
 * catch (e) {
 *   peekError(e)?.cause?.information?.type?.code.toString() === '123';
 *   peekError(e)?.cause?.message.toString() === 'Something went wrong';
 * }
 */
export function peekError(error: unknown): IPeekErrorWrapperObject | undefined {
  return error as IPeekErrorWrapperObject;
}
